import React, { useEffect, useRef, useState } from 'react'
import { Dropdown, Form, ListGroup } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { customFieldService } from '../../services/custom-field.service'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import { toastService } from '../../services/toast.service'
import { CustomField, CustomFieldInstance } from '../../types'
import { CustomFieldEditDialog } from '../CustomFields/CustomFieldEditDialog'
import './CustomFieldsDropdown.scss'

interface CustomFieldsDropdownProps {
  documentId?: number
  disabled?: boolean
  existingFields?: CustomFieldInstance[]
  onAdded?: (field: CustomField) => void
  onCreated?: (field: CustomField) => void
}

// Data type labels matching React implementation
export enum CustomFieldDataType {
  String = 'string',
  Url = 'url',
  Date = 'date',
  Boolean = 'boolean',
  Integer = 'integer',
  Float = 'float',
  Monetary = 'monetary',
  DocumentLink = 'documentlink',
  Select = 'select',
  LongText = 'longtext',
}

const DATA_TYPE_LABELS = [
  { id: CustomFieldDataType.Boolean, name: 'Boolean' },
  { id: CustomFieldDataType.Date, name: 'Date' },
  { id: CustomFieldDataType.Integer, name: 'Integer' },
  { id: CustomFieldDataType.Float, name: 'Number' },
  { id: CustomFieldDataType.Monetary, name: 'Monetary' },
  { id: CustomFieldDataType.String, name: 'Text' },
  { id: CustomFieldDataType.Url, name: 'Url' },
  { id: CustomFieldDataType.DocumentLink, name: 'Document Link' },
  { id: CustomFieldDataType.Select, name: 'Select' },
  { id: CustomFieldDataType.LongText, name: 'Long Text' },
]

const CustomFieldsDropdown: React.FC<CustomFieldsDropdownProps> = ({ disabled = false, existingFields = [], onAdded, onCreated }) => {
  const { t } = useTranslation()
  const [customFields, setCustomFields] = useState<CustomField[]>([])
  const [unusedFields, setUnusedFields] = useState<CustomField[]>([])
  const [filterText, setFilterText] = useState('')
  const [show, setShow] = useState(false)
  const [keyboardIndex, setKeyboardIndex] = useState<number>(-1)
  const filterInputRef = useRef<HTMLInputElement>(null)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [newFieldName, setNewFieldName] = useState('')

  // Load all custom fields
  useEffect(() => {
    loadCustomFields()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Update unused fields when existingFields change
  useEffect(() => {
    updateUnusedFields()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFields, existingFields])

  const loadCustomFields = async () => {
    if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.CustomField)) {
      setCustomFields([])
      return
    }
    try {
      const response = await customFieldService.listAll()
      setCustomFields(response.results || [])
    } catch (error) {}
  }

  const updateUnusedFields = () => {
    const existingFieldIds = existingFields.map((fi) => fi.field)
    // Separators are layout-only attributes — they are configured on the
    // document type, not attached to documents from this dropdown.
    const unused = customFields.filter(
      (f) => f.data_type !== 'separator' && !existingFieldIds.includes(f.id),
    )
    setUnusedFields(unused)
  }

  const filteredFields = unusedFields.filter((f) => !filterText || f.name.toLowerCase().includes(filterText.toLowerCase()))

  const handleAddField = (field: CustomField) => {
    if (disabled) return
    if (onAdded) {
      onAdded(field)
    }
    setShow(false)
    setFilterText('')
    setKeyboardIndex(-1)
  }

  const handleCreateField = () => {
    if (disabled) return
    setNewFieldName(filterText)
    setShowCreateDialog(true)
    setShow(false)
  }

  const handleDialogSuccess = async (newField?: CustomField) => {
    toastService.showInfo(t('successfully_created', { name: newField?.name }))
    setShowCreateDialog(false)
    setFilterText('')

    // Reload custom fields first
    await loadCustomFields()

    // Call onCreated callback to refresh parent component first
    if (onCreated && newField) {
      onCreated(newField)
    }

    // Then automatically add the new field to document
    if (!disabled && onAdded && newField) {
      // Use setTimeout to ensure the parent component has updated its state
      setTimeout(() => {
        onAdded(newField)
      }, 500)
    }
  }

  const handleDialogError = (error: any) => {
    toastService.showError(t('error_saving'), error)
  }

  const handleDialogClose = () => {
    setShowCreateDialog(false)
    setNewFieldName('')
  }

  const getDataTypeLabel = (dataType: string): string => {
    return DATA_TYPE_LABELS.find((l) => l.id === dataType)?.name || dataType
  }

  const handleOpenChange = (isOpen: boolean) => {
    setShow(isOpen)
    if (isOpen) {
      // Focus filter input when dropdown opens
      setTimeout(() => {
        if (filterInputRef.current) {
          filterInputRef.current.focus()
        }
      }, 100)
      setKeyboardIndex(-1)
    } else {
      setFilterText('')
      setKeyboardIndex(-1)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const totalItems = filteredFields.length + 1 // +1 for "Create new field"

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setKeyboardIndex((prev) => (prev < totalItems - 1 ? prev + 1 : prev))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setKeyboardIndex((prev) => (prev > -1 ? prev - 1 : -1))
    } else if (e.key === 'Enter' && keyboardIndex >= 0) {
      e.preventDefault()
      if (keyboardIndex < filteredFields.length) {
        handleAddField(filteredFields[keyboardIndex])
      } else {
        handleCreateField()
      }
    }
  }

  return (
    <>
      <Dropdown show={show} onToggle={handleOpenChange} className="me-2">
        <Dropdown.Toggle variant="outline-primary" size="sm" id="customFieldsDropdown" disabled={disabled}>
          <i className="bi bi-ui-radios"></i>
          <span className="d-none d-sm-inline">&nbsp;{t('custom_fields')}</span>
        </Dropdown.Toggle>

        <Dropdown.Menu className="shadow custom-fields-dropdown">
          <div onKeyDown={handleKeyDown}>
            <ListGroup variant="flush">
              {/* Search input */}
              <ListGroup.Item>
                <Form.Control
                  ref={filterInputRef}
                  type="text"
                  size="sm"
                  placeholder={t('search_fields')}
                  value={filterText}
                  onChange={(e) => {
                    setFilterText(e.target.value)
                    setKeyboardIndex(-1)
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </ListGroup.Item>

              {/* Field list */}
              {filteredFields.map((field, index) => (
                <ListGroup.Item
                  key={field.id}
                  action
                  className={`bg-light ${keyboardIndex === index ? 'active' : ''}`}
                  onClick={() => handleAddField(field)}
                  onMouseEnter={() => setKeyboardIndex(index)}
                >
                  <small className="d-flex">
                    {field.name}
                    <small className="ms-auto text-muted">{getDataTypeLabel(field.data_type)}</small>
                  </small>
                </ListGroup.Item>
              ))}

              {/* Create new field option */}
              {(!filterText || filteredFields.length === 0) && (
                <ListGroup.Item
                  action
                  className={`bg-light ${keyboardIndex === filteredFields.length ? 'active' : ''}`}
                  onClick={handleCreateField}
                  onMouseEnter={() => setKeyboardIndex(filteredFields.length)}
                  disabled={disabled}
                >
                  <small>
                    <i className="bi bi-asterisk" style={{ width: '.9em', height: '.9em' }}></i>
                    &nbsp;{t('create_new_field')}
                  </small>
                </ListGroup.Item>
              )}
            </ListGroup>
          </div>
        </Dropdown.Menu>
      </Dropdown>

      {/* Create Custom Field Dialog */}
      {showCreateDialog && (
        <CustomFieldEditDialog
          object={newFieldName ? ({ name: newFieldName } as any) : null}
          isCreating={true}
          onSuccess={handleDialogSuccess}
          onError={handleDialogError}
          onClose={handleDialogClose}
        />
      )}
    </>
  )
}

export default CustomFieldsDropdown
